import { EvalError } from "../simulation";
export function evalAnalysis(analysisSrc) {
    if (!analysisSrc) {
        return {
            outputs: [],
            plots: [],
        };
    }
    try {
        const parsed = JSON.parse(analysisSrc);
        return {
            outputs: Object.entries(parsed.outputs ?? {})
                .map((entry) => [
                entry[0],
                entry[1].map((op) => op.op === "filter" && Array.isArray(op.value)
                    ? op.value.map((val) => ({ ...op, value: val }))
                    : [op]),
            ])
                .flatMap((entry) => entry[1].reduce((ops, op) => ops.flatMap(([name, opsa]) => op.length > 1
                ?
                    op.map((op) => [
                        name + "_" + op.value,
                        opsa.concat([op]),
                    ])
                :
                    [[name, opsa.concat(op)]]), [[entry[0], []]]))
                .map(([name, ops]) => ({
                name,
                fn: opChain(ops),
            })),
            plots: parsed.plots?.map((plotDefinition, i) => ({
                ...plotDefinition,
                layout: {
                    width: "50%",
                    height: "40%",
                    ...plotDefinition.layout,
                },
                position: {
                    x: i % 2 == 0 ? "0%" : "50%",
                    y: `${Math.floor(i / 2) * 40}%`,
                    ...plotDefinition.position,
                },
            })) ?? [],
        };
    }
    catch (e) {
        throw new EvalError(e, "analysis.json");
    }
}
const nestedAccess = (accessor, val) => accessor.split(".").reduce((result, prop) => result[prop], val);
function opChain(ops) {
    return ops.reduce((fn, op) => (arr) => OpFn(op)(fn(arr)), ((state) => state));
}
const OpFn = (op) => {
    switch (op.op) {
        case "count":
            return (as) => as.length;
        case "sum":
            return (as) => as.reduce((a, b) => a + b, 0);
        case "min":
            return (as) => Math.min(...as);
        case "max":
            return (as) => Math.max(...as);
        case "mean":
            return (as) => as.reduce((a, b) => a + b, 0) / as.length;
        case "movingAverage":
            return (as) => as.slice(-op.window).reduce((a, b) => a + b, 0) / as.length;
        case "get":
            return (as) => as.map((a) => a[op.field]).filter((a) => a !== undefined);
        case "filter":
            return (as) => as.filter((a) => ComparisonFn[op.comparison](nestedAccess(op.field, a), op.value));
    }
};
const ComparisonFn = {
    eq: (a, b) => a === b,
    neq: (a, b) => a !== b,
    lt: (a, b) => a < b,
    lte: (a, b) => a <= b,
    gt: (a, b) => a > b,
    gte: (a, b) => a >= b,
};
function outputs(...args) {
    const ret = [];
    for (let i = 0; i < args.length; i += 2) {
        const name = args[i];
        let fn = args[i + 1];
        if (fn.done) {
            fn = fn.done();
        }
        ret.push({
            name,
            fn,
        });
    }
    return ret;
}
function splitBy(field, options, rawFn) {
    const fn = rawFn instanceof Chain ? rawFn.done() : rawFn;
    const ret = [];
    function match(value, option) {
        if (Array.isArray(value)) {
            return value.includes(option);
        }
        else {
            return value === option;
        }
    }
    for (const option of options) {
        ret.push(option);
        ret.push((list, series, context) => {
            return fn(list.filter((a) => match(a[field], option)), series, context);
        });
    }
    return ret;
}
Object.defineProperty(Array.prototype, "suffix", {
    enumerable: true,
    configurable: true,
    writable: true,
    value(suffix) {
        this.forEach((_, i, arr) => {
            if (i % 2 === 0) {
                arr[i] += suffix;
            }
        });
        return this;
    },
});
Object.defineProperty(Array.prototype, "prefix", {
    enumerable: true,
    configurable: true,
    writable: true,
    value(prefix) {
        this.forEach((_, i, arr) => {
            if (i % 2 === 0) {
                arr[i] = prefix + arr[i];
            }
        });
        return this;
    },
});
function exists(x) {
    return x !== null && x !== undefined;
}
class Chain {
    constructor(fns = [], newFn) {
        this.fns = [...fns];
        if (newFn) {
            this.fns.push(newFn);
        }
    }
    done() {
        return (list, series, context) => {
            let output = list;
            for (const fn of this.fns) {
                output = fn(output, series, context);
            }
            return output;
        };
    }
    filterBy(field, value) {
        return new Chain(this.fns, (list) => {
            return list.filter((el) => el[field] === value);
        });
    }
    count() {
        return new Chain(this.fns, (list) => list.length);
    }
    get(field) {
        return new Chain(this.fns, (list) => {
            return list.map((el) => el[field]);
        });
    }
    sum(field) {
        function get(el) {
            return field ? el[field] : el;
        }
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(get(el)));
            return filtered.reduce((acc, el) => acc + get(el), 0);
        });
    }
    mean(field) {
        function get(el) {
            return field ? el[field] : el;
        }
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(get(el)));
            return filtered.reduce((acc, el) => acc + get(el), 0) / filtered.length;
        });
    }
    max(field) {
        function get(el) {
            return field ? el[field] : el;
        }
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(get(el)));
            return filtered.reduce((acc, el) => Math.max(acc, get(el)), -Infinity);
        });
    }
    min(field) {
        function get(el) {
            return field ? el[field] : el;
        }
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(get(el)));
            return filtered.reduce((acc, el) => Math.min(acc, get(el)), Infinity);
        });
    }
    lt(field, value) {
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(el[field]));
            return filtered.filter((el) => el[field] < value);
        });
    }
    lte(field, value) {
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(el[field]));
            return filtered.filter((el) => el[field] <= value);
        });
    }
    eq(field, value) {
        return new Chain(this.fns, (list) => {
            return list.filter((el) => el[field] === value);
        });
    }
    gt(field, value) {
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(el[field]));
            return filtered.filter((el) => el[field] > value);
        });
    }
    gte(field, value) {
        return new Chain(this.fns, (list) => {
            const filtered = list.filter((el) => exists(el[field]));
            return filtered.filter((el) => el[field] >= value);
        });
    }
    movingAverage(output, options = { window: 15 }) {
        return new Chain(this.fns, (_, series) => {
            const slice = series[output].slice(-options.window);
            return (slice.reduce((acc, x) => acc + (x && !(x instanceof Array) ? x : 0), 0) / slice.length);
        });
    }
}
//# sourceMappingURL=evalAnalysis.js.map